import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
} from "react";
import { useCollection } from "react-firebase-hooks/firestore";
import {
  query,
  collection,
  where,
  updateDoc,
  doc,
  addDoc,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { firestore } from "@/firebase";
import {
  Cart,
  CartSave,
  createCart,
  TravelProduct,
  TangoProduct,
  ZincProduct,
} from "@t3/shared";
import convertToPoints from "../utils/convertToPoints";
import { useAuthContext } from "./AuthContext";
import { cartConverter } from "@/converters";

interface CartContextType {
  cart: Cart | null;
  totalItemCount: number;
  totalCostPoints: number;
  cartUpdateLoading: boolean;
  cartUpdateError: string;
  addTangoItem: (product: TangoProduct) => Promise<void>;
  addTravelItem: (product: TravelProduct) => Promise<void>;
  addZincItem: (product: ZincProduct) => Promise<void>;
  removeTangoItem: (utid: string, index: number) => Promise<void>;
  removeTravelItem: (utid: string, index: number) => Promise<void>;
  removeZincItem: (utid: string, index: number) => Promise<void>;
  submitForPurchase: () => Promise<any>;
}

const CartContext = createContext<CartContextType>({} as CartContextType);

export const useCartContext = () => useContext(CartContext);

export const CartProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuthContext();
  const [cartRef] = useState(
    collection(
      firestore,
      `company/${user.company}/users/${user.id}/cart`,
    ).withConverter(cartConverter),
  );
  const [cartSnapshot, cartLoading, cartError] = useCollection(
    query(
      cartRef,
      where("status", "==", "draft"),
      orderBy("createdAt", "asc"),
    ).withConverter(cartConverter),
  );
  const isCreatingCart = useRef(false);
  const [cartUpdateLoading, setCartUpdateLoading] = useState(false);
  const [cartUpdateError, setCartUpdateError] = useState("");

  const cart = cartSnapshot?.docs[0]?.data();

  useEffect(() => {
    if (!cart && !cartLoading && !cartError && !isCreatingCart.current) {
      //preload a cart
      ensureCartExists();
    }
  }, [cart, cartLoading]);

  const ensureCartExists = async (): Promise<Cart> => {
    // If cart already exists, return it
    if (cart) {
      return cart;
    }

    // If still loading, wait a bit and check again
    if (cartLoading) {
      throw new Error("Cart is still loading"); //todo; probs some better handling possible
    }

    if (isCreatingCart.current) {
      throw new Error("Cart is already being created"); //todo; probs some better handling possible
    }

    isCreatingCart.current = true;

    // Create a new cart
    const newCartData: CartSave = createCart({
      status: "draft",
      refundedAt: null,
      items: {
        tangoItems: null,
        travelItems: null,
        zincItems: null,
      },
      init: null,
      purchaseResults: null,
    });

    const docRef = await addDoc(cartRef, newCartData);
    const newCart = await getDoc(docRef);
    isCreatingCart.current = false;
    return newCart.data() as Cart;
  };

  const addItem = async (
    type: "tangoItems" | "travelItems" | "zincItems",
    product: TangoProduct | TravelProduct | ZincProduct,
  ) => {
    setCartUpdateLoading(true);
    setCartUpdateError("");

    try {
      // Ensure cart exists (create if needed)
      const currentCart = await ensureCartExists();
      const existingCartRef = doc(cartRef, currentCart.id);

      const currentItems = currentCart.items[type] || {};
      const newItems = {
        ...currentItems,
        [product.utid]: [...(currentItems[product.utid] || []), product],
      };

      const newCartData: Partial<Cart> = {
        items: {
          ...currentCart.items,
          [type]: newItems,
        },
      };

      await updateDoc(existingCartRef, newCartData);
    } catch (e) {
      setCartUpdateError(`${e}`);
      throw e;
    } finally {
      setCartUpdateLoading(false);
    }
  };

  const addTangoItem = async (product: TangoProduct) => {
    await addItem("tangoItems", product);
  };
  const addTravelItem = async (product: TravelProduct) => {
    await addItem("travelItems", product);
  };
  const addZincItem = async (product: ZincProduct) => {
    await addItem("zincItems", product);
  };

  const removeItem = async (
    type: "tangoItems" | "travelItems" | "zincItems",
    utid: string,
    index: number,
  ) => {
    setCartUpdateLoading(true);
    setCartUpdateError("");

    try {
      if (!cart) {
        throw new Error("No cart found");
      }

      const existingCartRef = doc(cartRef, cart.id);

      if (!cart.items[type]?.[utid]) {
        throw new Error("Item not found");
      }

      const currentItems = cart.items[type] || {};
      const newTypeItems = {
        ...currentItems,
        [utid]: currentItems[utid]!.filter((_, i) => i !== index),
      };

      if (newTypeItems[utid]?.length === 0) {
        delete newTypeItems[utid];
      }

      const newCartData: Partial<Cart> = {
        items: {
          ...cart.items,
          [type]: newTypeItems,
        },
      };

      await updateDoc(existingCartRef, newCartData);
    } catch (e) {
      setCartUpdateError(`${e}`);
      throw e;
    } finally {
      setCartUpdateLoading(false);
    }
  };

  const removeTangoItem = async (utid: string, index: number) => {
    await removeItem("tangoItems", utid, index);
  };
  const removeTravelItem = async (utid: string, index: number) => {
    await removeItem("travelItems", utid, index);
  };
  const removeZincItem = async (utid: string, index: number) => {
    await removeItem("zincItems", utid, index);
  };

  const submitForPurchase = async () => {
    setCartUpdateLoading(true);
    setCartUpdateError("");

    try {
      if (!cart) {
        throw new Error("No cart found");
      }

      if (!user.id || !user.company) {
        throw new Error("User information is missing");
      }

      const existingCartRef = doc(cartRef, cart.id);

      const newCartData: Partial<Cart> = {
        status: "submitted",
        init: null,
      };

      await updateDoc(existingCartRef, newCartData);

      setCartUpdateLoading(false);
      return { success: true, id: cart.id };
    } catch (e) {
      setCartUpdateLoading(false);
      setCartUpdateError(`${e}`);
      throw e;
    }
  };

  if (cartLoading) {
    return <div>Loading...</div>;
  }

  if (cartError) {
    throw new Error(
      `Error loading cart: ${cartError.message || "Unknown error"}`,
    );
  }

  const cartProducts: (TangoProduct | TravelProduct | ZincProduct)[] = [
    ...Object.values(cart?.items?.tangoItems ?? {}).flat(),
    ...Object.values(cart?.items?.travelItems ?? {}).flat(),
    ...Object.values(cart?.items?.zincItems ?? {}).flat(),
  ];

  const { totalItemCount, totalCostPoints } = cartProducts.reduce(
    (totals, product) => {
      // Check if product is ZincProduct (has quantity property)
      const isZincProduct = "quantity" in product;
      const quantity = isZincProduct ? (product as ZincProduct).quantity : 1;
      const productCostPoints = Math.round(
        convertToPoints(product.amount) * quantity,
      );

      return {
        totalItemCount: Math.round(totals.totalItemCount + quantity),
        totalCostPoints: Math.round(totals.totalCostPoints + productCostPoints),
      };
    },
    { totalItemCount: 0, totalCostPoints: 0 },
  );

  const value: CartContextType = {
    cart: cart ?? null,
    totalItemCount,
    totalCostPoints,
    cartUpdateLoading,
    cartUpdateError,
    addTangoItem,
    addTravelItem,
    addZincItem,
    removeTangoItem,
    removeTravelItem,
    removeZincItem,
    submitForPurchase,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
